import React, { FC, useState, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// Components
import { Modal, Table, Toast, Button as SMButton } from '../../../components';
import { Close, Save, Add } from '@mui/icons-material';
import { Fade, Typography, Button, ButtonGroup, Grid, TextField } from '@mui/material';
import { IGroup, IGroupMember } from '../../../models';
import { updateAdminGroupUsers } from '../../../fetch';
import { isEmail } from '../../../helpers';

interface IGroupModal {
  open: boolean;
  onClose: () => void;
  currentGroup?: IGroup;
  fetchGroups: () => {};
}

// Create an editable cell renderer
const EditableCell = ({
  value: initialValue,
  row,
  column,
  updateMyData // This is a custom function that we supplied to our table instance
}) => {
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState<string>(initialValue);
  const onChange = e => {
    setValue(e.target.value);
  };

  // We'll only update the external data when the input is blurred
  const onBlur = () => {
    updateMyData(row.original, column.id, value);
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      onBlur={onBlur}
      placeholder={column.Header}
      autoComplete="nope"
      value={value}
      required
      onChange={onChange}
      error={value === '' || (column.id === 'memberEmailAddress' && !isEmail(value))}
      helperText={
        value === ''
          ? 'Required'
          : column.id === 'memberEmailAddress' && !isEmail(value)
          ? 'Invalid Email'
          : ''
      }
    />
  );
};

export const GroupModal: FC<IGroupModal> = ({ open, onClose, currentGroup, fetchGroups }) => {
  const classes = useStyles();
  const [isError, showError] = useState<boolean>(false);
  const [isSuccess, showSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [modifiedUsers, setModifiedUsers] = useState<IGroupMember[]>(currentGroup.users);
  const [isSaving, setSaving] = useState<boolean>(false);

  const updateMyData = (user: IGroupMember, columnId: string, value: string, error?: boolean) => {
    setModifiedUsers(old => {
      return old.map(row => {
        if (row.memberEmailAddress === user.memberEmailAddress) {
          return {
            ...user,
            [columnId]: value
          };
        }
        return row;
      });
    });
  };

  const columns = [
    {
      Header: 'Username',
      accessor: 'memberUserName',
      sort: false,
      Cell: EditableCell
    },
    {
      Header: 'Email',
      accessor: 'memberEmailAddress',
      sort: false,
      Cell: EditableCell
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IGroupMember } };
      }) => {
        return (
          <>
            <Button
              id="remove"
              color="primary"
              startIcon={<Close />}
              className={classes.deleteButton}
              onClick={() => {
                setModifiedUsers(
                  modifiedUsers.filter(
                    user => user.memberEmailAddress !== original.memberEmailAddress
                  )
                );
              }}
            >
              Remove
            </Button>
          </>
        );
      }
    }
  ];
  // there are empty editable users in the table
  const hasEmptyValues =
    modifiedUsers.filter(user => !user.memberUserName || !user.memberEmailAddress).length > 0;

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          onClose();
        }}
        maxWidth="md"
      >
        <Fade in={open}>
          <div>
            <Typography variant="h5">Add & Remove Users To {currentGroup.name}</Typography>
            <div className={classes.content}>
              <Table
                columns={columns}
                data={modifiedUsers}
                hidePagination
                useTableProps={{
                  updateMyData
                }}
              />
              {hasEmptyValues && (
                <Typography align="center" className={classes.validationMessage}>
                  Table has empty values, please fill in the missing fields.
                </Typography>
              )}
              <Grid container alignItems="center" className={classes.saveWrapper}>
                <div className={classes.addButton}>
                  <Button
                    id="remove"
                    color="primary"
                    variant="contained"
                    disabled={hasEmptyValues}
                    startIcon={<Add />}
                    onClick={() => {
                      setModifiedUsers([
                        ...modifiedUsers,
                        {
                          memberUserName: '',
                          memberEmailAddress: ''
                        }
                      ]);
                    }}
                  >
                    Add User
                  </Button>
                </div>
                <ButtonGroup>
                  <SMButton
                    id="save"
                    color="primary"
                    variant="contained"
                    loading={isSaving ? 'Saving...' : ''}
                    disabled={hasEmptyValues}
                    startIcon={<Save />}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await updateAdminGroupUsers({
                          ...currentGroup,
                          users: modifiedUsers
                        });
                        onClose();
                        showSuccess(true);
                        setSuccessMessage('Users Updated!');
                        fetchGroups();
                      } catch (error) {
                        showError(true);
                        setErrorMessage('Error updating users.');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Save
                  </SMButton>
                  <Button
                    id="remove"
                    variant="contained"
                    startIcon={<Close />}
                    onClick={() => {
                      onClose();
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Grid>
            </div>
          </div>
        </Fade>
      </Modal>
      <Toast
        id="user-success"
        message={successMessage || `Users Updated!`}
        open={isSuccess}
        onClose={() => showSuccess(false)}
        variant="success"
      />
      <Toast
        id="user-error"
        autoHideDuration={5000}
        message={
          errorMessage || 'We were unable to update the users at this time. Please try again later.'
        }
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(1)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  addButton: {
    flex: 1
  },
  validationMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  }
}));
